import { getCommonWidget } from '../../dashWidgetHelper'
import { commonWidgets, NETPAY_COLORS_NPS, NETPAY_SCALE_3_COLORS } from '../commonWidgetsNetpay'
import { List } from 'immutable';

// --------------------------------------------------------
// DICCIONARIOS DE ETIQUETAS
// --------------------------------------------------------
const CSAT_LABELS: Record<string, string> = {
    "5": "Extremadamente satisfecho",
    "4": "Satisfecho",
    "3": "Ni satisfecho ni insatisfecho",
    "2": "Insatisfecho",
    "1": "Extremadamente insatisfecho",
    "0": "NS / NC"
};

const CES_LABELS: Record<string, string> = {
    "5": "Extremadamente fácil",
    "4": "Fácil",
    "3": "Ni fácil ni difícil",
    "2": "Difícil",
    "1": "Extremadamente complicado",
    "0": "NS / NC"
};

// --------------------------------------------------------
// FUNCIÓN HELPER (Mapeo de datos para pasteles)
// --------------------------------------------------------
const mapWithLabels = (data: any, dict: Record<string, string>) => {
    if (!Array.isArray(data)) return [];

    return data.map((item: any, index: number, array: any[]) => {
        const keyCandidate = item.groupId ?? item.groupName ?? item.value ?? item.name;
        const key = keyCandidate !== undefined && keyCandidate !== null ? String(keyCandidate).trim() : '';

        let labelText = key;

        if (dict && dict[key]) {
            labelText = dict[key];
        } else if (item.groupName && String(item.groupName).trim() !== '') {
            labelText = String(item.groupName);
        }

        if (!labelText || labelText === 'undefined') labelText = `Sin respuesta`;

        const count = item.count !== undefined ? item.count : 0;
        let finalValue = item.value;

        if (array.length === 1) {
            finalValue = 100;
        } else if (finalValue === undefined) {
            finalValue = count;
        }

        return {
            ...item,
            value: finalValue,
            groupName: labelText,
            label: count > 0 ? `${labelText} (${count})` : labelText
        };
    });
};

const NETPAY_SCALE_5_COLORS: any = {
    "Extremadamente satisfecho": '#22a06b',
    "Satisfecho": '#a3d977',
    "Ni satisfecho ni insatisfecho": '#f5a524',
    "Insatisfecho": '#f08591',
    "Extremadamente insatisfecho": '#e74c5c',

    "Extremadamente fácil": '#22a06b',
    "Fácil": '#a3d977',
    "Ni fácil ni difícil": '#f5a524',
    "Difícil": '#f08591',
    "Extremadamente complicado": '#e74c5c',

    "NS / NC": '#cccccc',
    1: '#e74c5c',
    2: '#f08591',
    3: '#f5a524',
    4: '#a3d977',
    5: '#22a06b',
};

// --------------------------------------------------------
// CSAT y CES en porcentaje (netpay-only)
// --------------------------------------------------------

const computeDetailsStats = (details: any[]) => {
    let weightedScoreSum = 0 // suma de (categoría x su % de respuestas), para CES
    let totalPercent = 0     // suma de los % de las categorías 1 a 5 (excluye NS/NC)
    let positivePercent = 0  // suma de los % de las categorías 4 y 5, para CSAT

    details.forEach((item: any) => {
        const groupId = Number(item.groupId)
        const percent = Number(item.value) || 0 // % que ya trae el backend para esta categoría

        if (!isNaN(groupId) && groupId > 0) {
            totalPercent += percent
            weightedScoreSum += groupId * percent
            if (groupId === 4 || groupId === 5) {
                positivePercent += percent
            }
        }
    })

    return { weightedScoreSum, totalPercent, positivePercent }
}

const mapGaugeDataPercent = (data: any, dict: Record<string, string>, percentMode: 'csat' | 'ces') => {
    const actualData = Array.isArray(data) ? data[0] : data;
    if (!actualData || !actualData.details) return actualData;

    let details = actualData.details;

    // Sort by groupId descending (5, 4, 3, 2, 1)
    let sortedDetails = [...details].sort((a: any, b: any) => (b.groupId || 0) - (a.groupId || 0));

    // Map labels (igual que antes, para los segmentos del semicírculo)
    sortedDetails = sortedDetails.map((item: any) => {
        const key = item.groupId ? String(item.groupId).trim() : '';
        let labelText = key;
        if (dict && dict[key]) {
            labelText = dict[key];
        }

        return {
            ...item,
            groupName: labelText,
            label: labelText,
            name: labelText,
            group: labelText,
            groupId: labelText
        };
    });

    const { weightedScoreSum, totalPercent, positivePercent } = computeDetailsStats(details);

    const percentValue = totalPercent > 0
        ? (percentMode === 'csat'
            ? positivePercent // ej. 74 + 11
            : ((weightedScoreSum / totalPercent) / 5) * 100)
        : actualData.value;

    return {
        ...actualData,
        details: sortedDetails,
        value: percentValue
    };
};

export default {
    title: 'CSAT - CES',
    description: 'Dashboard de resultados Encuesta CSAT - CES',
    backgroundClassName: (filters: any) => 'epsilon-hero-bg',
    widgets: (filters: any, filtersData: Record<string, any>) => {
        return [

            // -----------------------------------------------------------
            // SATISFACCIÓN Y ESFUERZO
            // -----------------------------------------------------------
            { title: 'MÉTRICAS PRINCIPALES', type: 'label', size: [4, 1], config: { color: '#B7AADC' } },

            getCommonWidget({
                commonWidgets,
                name: 'countNumberSemicircle',
                title: '¿Qué tan satisfecho estás con el servicio proporcionado por NetPay?',
                size: [2, 2],
                key: 'csat-satisfaccion',
                indicator: 'np_csat_ces_satisfaccion',
                extras: { formula: 'count-number', model: 'avg' }, // misma fórmula/backend de siempre
                mapper: (data: any) => mapGaugeDataPercent(data, CSAT_LABELS, 'csat'),
                extraConfigs: {
                    showValue: true,
                    valuePosition: 'inner',
                    center: ['50%', '75%'],
                    radius: ['60%', '115%'],
                    decimals: 1,
                    itemHeight: 14,
                    padAngle: 10,
                    colors: NETPAY_SCALE_5_COLORS,
                    itemBorderRadius: 5,
                    legendOrient: 'horizontal',
                    fontSize: 45,
                    showCenterSymbol: true, // muestra "%" también en el número central
                }
            }),

            getCommonWidget({
                commonWidgets,
                name: 'countNumberSemicircle',
                title: '¿Qué tanto esfuerzo te tomó que tu solicitud fuera resuelta?',
                size: [2, 2],
                key: 'ces-facilidad',
                indicator: 'np_csat_ces_facilidad',
                extras: { formula: 'count-number', model: 'avg' }, // misma fórmula/backend de siempre
                mapper: (data: any) => mapGaugeDataPercent(data, CES_LABELS, 'ces'),
                extraConfigs: {
                    showValue: true,
                    valuePosition: 'inner',
                    center: ['50%', '75%'],
                    radius: ['60%', '115%'],
                    decimals: 1,
                    itemHeight: 14,
                    padAngle: 10,
                    colors: NETPAY_SCALE_5_COLORS,
                    itemBorderRadius: 5,
                    legendOrient: 'horizontal',
                    fontSize: 45,
                    showCenterSymbol: true, // muestra "%" también en el número central
                }
            }),

            // -----------------------------------------------------------
            // TOP 5 CSAT POR MOTIVO (mejores / peores)
            // -----------------------------------------------------------

            { title: 'TOP 5 CSAT POR MOTIVO DE NIVEL DE SERVICIO', type: 'label', size: [4, 1], config: { color: '#B7AADC' } },

            {
                title: 'CSAT - Top 5 Mejores Motivos',
                size: [2, 2],
                className: 'csat-ranking-card',
                config: {
                    id: 'csat-top5-mejores-motivo',
                    type: 'ranking',
                    rankingType: 'box',
                    showIndex: true,
                    maxSlice: 5,
                    limit: 5,
                    decimals: 1,
                    symbol: '%', // CSAT (aproximación) = promedio 1-5 / 5 * 100
                    color: '#22a06b',
                    titleBackgroundColor: '#22a06b',
                    mapper: (data: any) => {
                        if (typeof document !== 'undefined' && !document.getElementById('csat-ranking-dark-bg')) {
                            const style = document.createElement('style');
                            style.id = 'csat-ranking-dark-bg';
                            style.innerHTML = `
                              .csat-ranking-card .ranking-component { background-color: #3B1D52; border-radius: 13px; padding: 8px 16px 4px; margin-top: 12px; }
                              .csat-ranking-card .title-container-rating-component { width: 100%; }
                          `;
                            document.head.appendChild(style);
                        }
                        return (data ?? [])
                            .map((item: any) => {
                                const rawLabel = item.group ?? item.groupName
                                const baseLabel = rawLabel ? String(rawLabel).trim() : 'No Definido'
                                const count = item.count ?? 0
                                const rawValue = Number(item.value) || 0
                                const percentValue = (rawValue / 5) * 100
                                return { ...item, label: `${baseLabel} (${count})`, value: percentValue }
                            })
                            .asMutable()
                            .sort((a: any, b: any) => (b.value ?? 0) - (a.value ?? 0))
                            .slice(0, 5)
                    },
                    indicators: [
                        {
                            indicator: 'np_csat_ces_satisfaccion',
                            keyExtract: ['value', 'group', 'count', 'groupName'],
                            label: 'groupName',
                            grouped: 'question',
                            value: 'value',
                            singleton: true,
                            extras: {
                                groupNameFormat: 'description',
                                formula: 'wavg-5',
                                model: 'avg',
                                groupByResponseValueId: 17200, // NP_Motivo_de_Nivel_de_Servicio__c
                                groupBy: 'responseValue',
                            },
                        },
                    ],
                },
            },

            {
                title: 'CSAT - Top 5 Motivos a Mejorar',
                size: [2, 2],
                className: 'csat-ranking-card',
                config: {
                    id: 'csat-top5-peores-motivo',
                    type: 'ranking',
                    rankingType: 'box',
                    showIndex: true,
                    maxSlice: 5,
                    limit: 5,
                    decimals: 1,
                    symbol: '%',
                    color: '#e74c5c',
                    titleBackgroundColor: '#e74c5c',
                    mapper: (data: any) => {
                        if (typeof document !== 'undefined' && !document.getElementById('csat-ranking-dark-bg')) {
                            const style = document.createElement('style');
                            style.id = 'csat-ranking-dark-bg';
                            style.innerHTML = `
                              .csat-ranking-card .ranking-component { background-color: #3B1D52; border-radius: 13px; padding: 8px 16px 4px; margin-top: 12px; }
                              .csat-ranking-card .title-container-rating-component { width: 100%; }
                          `;
                            document.head.appendChild(style);
                        }
                        return (data ?? [])
                            .map((item: any) => {
                                const rawLabel = item.group ?? item.groupName
                                const baseLabel = rawLabel ? String(rawLabel).trim() : 'No Definido'
                                const count = item.count ?? 0
                                const rawValue = Number(item.value) || 0
                                const percentValue = (rawValue / 5) * 100
                                return { ...item, label: `${baseLabel} (${count})`, value: percentValue }
                            })
                            .asMutable()
                            .sort((a: any, b: any) => (a.value ?? 0) - (b.value ?? 0))
                            .slice(0, 5)
                    },
                    indicators: [
                        {
                            indicator: 'np_csat_ces_satisfaccion',
                            keyExtract: ['value', 'group', 'count', 'groupName'],
                            label: 'groupName',
                            grouped: 'question',
                            value: 'value',
                            singleton: true,
                            extras: {
                                groupNameFormat: 'description',
                                formula: 'wavg-5',
                                model: 'avg',
                                groupByResponseValueId: 17200, // NP_Motivo_de_Nivel_de_Servicio__c
                                groupBy: 'responseValue',
                            },
                        },
                    ],
                },
            },

            // -----------------------------------------------------------
            // CSAT Y CES POR MOTIVO DE NIVEL DE SERVICIO
            // -----------------------------------------------------------
            { title: 'CSAT Y CES POR MOTIVO DE NIVEL DE SERVICIO', type: 'label', size: [4, 1], config: { color: '#B7AADC' } },

            getCommonWidget({
                commonWidgets,
                name: 'barByQuestion',
                title: 'CSAT por Motivo de Nivel de Servicio',
                size: [4, 5], // antes [4, 3]: más filas de alto = barras más altas verticalmente
                key: 'csat-por-motivo',
                indicator: 'np_csat_ces_satisfaccion',
                extras: {
                    formula: 'wavg-5',
                    model: 'avg',
                    groupByResponseValueId: 17200, // NP_Motivo_de_Nivel_de_Servicio__c
                    groupBy: 'responseValue',
                },
                mapper: (data: any) => {

                    return (data ?? [])
                        .map((item: any) => {
                            const rawLabel = item.group ?? item.groupName
                            const baseLabel = rawLabel ? String(rawLabel).trim() : 'No Definido'

                            const count = item.count ?? 0
                            const label = `${baseLabel} (${count})`

                            const rawValue = Number(item.value) || 0

                            const percentValue = (rawValue / 5) * 100
                            return { ...item, group: label, groupName: label, label, value: percentValue }
                        })
                        .asMutable()
                        .sort((a: any, b: any) => (b.value ?? 0) - (a.value ?? 0))
                },
                extraConfigs: {
                    colors: NETPAY_COLORS_NPS,
                    decimals: 1,
                    valueSymbol: '%', // el valor de cada barra ya viene convertido a % en el mapper
                    showValues: true,
                    wrapTexts: true,
                    // Vertical (de arriba hacia abajo) para que se alcancen
                    // a leer los 54 nombres de motivo sin traslaparse.
                    rotateLabel: 90,
                    rotateXAxisLegend: true,
                    itemsWidth: 120,

                    denseBars: true,
                    showLegend: false,
                }
            }),

            // -----------------------------------------------------------
            // TOP 5 CES POR MOTIVO (mejores / peores) — en porcentaje
            // -----------------------------------------------------------
            { title: 'TOP 5 CES POR MOTIVO DE NIVEL DE SERVICIO', type: 'label', size: [4, 1], config: { color: '#B7AADC' } },

            {
                title: 'CES - Top 5 Mejores Motivos',
                size: [2, 2],
                className: 'csat-ranking-card',
                config: {
                    id: 'ces-top5-mejores-motivo',
                    type: 'ranking',
                    rankingType: 'box',
                    showIndex: true,
                    maxSlice: 5,
                    limit: 5,
                    decimals: 1,
                    symbol: '%', // CES = promedio 1-5 / 5 * 100
                    color: '#22a06b',
                    titleBackgroundColor: '#22a06b',
                    mapper: (data: any) => {
                        if (typeof document !== 'undefined' && !document.getElementById('csat-ranking-dark-bg')) {
                            const style = document.createElement('style');
                            style.id = 'csat-ranking-dark-bg';
                            style.innerHTML = `
                              .csat-ranking-card .ranking-component { background-color: #3B1D52; border-radius: 13px; padding: 8px 16px 4px; margin-top: 12px; }
                              .csat-ranking-card .title-container-rating-component { width: 100%; }
                          `;
                            document.head.appendChild(style);
                        }
                        return (data ?? [])
                            .map((item: any) => {
                                const rawLabel = item.group ?? item.groupName
                                const baseLabel = rawLabel ? String(rawLabel).trim() : 'No Definido'
                                const count = item.count ?? 0
                                const rawValue = Number(item.value) || 0
                                const percentValue = (rawValue / 5) * 100
                                return { ...item, label: `${baseLabel} (${count})`, value: percentValue }
                            })
                            .asMutable()
                            .sort((a: any, b: any) => (b.value ?? 0) - (a.value ?? 0))
                            .slice(0, 5)
                    },
                    indicators: [
                        {
                            indicator: 'np_csat_ces_facilidad',
                            keyExtract: ['value', 'group', 'count', 'groupName'],
                            label: 'groupName',
                            grouped: 'question',
                            value: 'value',
                            singleton: true,
                            extras: {
                                groupNameFormat: 'description',
                                formula: 'wavg-5',
                                model: 'avg',
                                groupByResponseValueId: 17200, // NP_Motivo_de_Nivel_de_Servicio__c
                                groupBy: 'responseValue',
                            },
                        },
                    ],
                },
            },

            {
                title: 'CES - Top 5 Motivos a Mejorar',
                size: [2, 2],
                className: 'csat-ranking-card',
                config: {
                    id: 'ces-top5-peores-motivo',
                    type: 'ranking',
                    rankingType: 'box',
                    showIndex: true,
                    maxSlice: 5,
                    limit: 5,
                    decimals: 1,
                    symbol: '%',
                    color: '#e74c5c',
                    titleBackgroundColor: '#e74c5c',
                    mapper: (data: any) => {
                        if (typeof document !== 'undefined' && !document.getElementById('csat-ranking-dark-bg')) {
                            const style = document.createElement('style');
                            style.id = 'csat-ranking-dark-bg';
                            style.innerHTML = `
                              .csat-ranking-card .ranking-component { background-color: #3B1D52; border-radius: 13px; padding: 8px 16px 4px; margin-top: 12px; }
                              .csat-ranking-card .title-container-rating-component { width: 100%; }
                          `;
                            document.head.appendChild(style);
                        }
                        return (data ?? [])
                            .map((item: any) => {
                                const rawLabel = item.group ?? item.groupName
                                const baseLabel = rawLabel ? String(rawLabel).trim() : 'No Definido'
                                const count = item.count ?? 0
                                const rawValue = Number(item.value) || 0
                                const percentValue = (rawValue / 5) * 100
                                return { ...item, label: `${baseLabel} (${count})`, value: percentValue }
                            })
                            .asMutable()
                            .sort((a: any, b: any) => (a.value ?? 0) - (b.value ?? 0))
                            .slice(0, 5)
                    },
                    indicators: [
                        {
                            indicator: 'np_csat_ces_facilidad',
                            keyExtract: ['value', 'group', 'count', 'groupName'],
                            label: 'groupName',
                            grouped: 'question',
                            value: 'value',
                            singleton: true,
                            extras: {
                                groupNameFormat: 'description',
                                formula: 'wavg-5',
                                model: 'avg',
                                groupByResponseValueId: 17200, // NP_Motivo_de_Nivel_de_Servicio__c
                                groupBy: 'responseValue',
                            },
                        },
                    ],
                },
            },

            getCommonWidget({
                commonWidgets,
                name: 'barByQuestion',
                title: 'CES por Motivo de Nivel de Servicio',
                size: [4, 5],
                key: 'ces-por-motivo',
                indicator: 'np_csat_ces_facilidad',
                extras: {
                    formula: 'wavg-5',
                    model: 'avg',
                    groupByResponseValueId: 17200, // NP_Motivo_de_Nivel_de_Servicio__c
                    groupBy: 'responseValue',
                },
                mapper: (data: any) => {

                    return (data ?? [])
                        .map((item: any) => {
                            const rawLabel = item.group ?? item.groupName
                            const baseLabel = rawLabel ? String(rawLabel).trim() : 'No Definido'
                            const count = item.count ?? 0
                            const label = `${baseLabel} (${count})`
                            // CES = promedio 1-5 / 5 * 100 (misma fórmula que el gauge CES)
                            const rawValue = Number(item.value) || 0
                            const percentValue = (rawValue / 5) * 100
                            return { ...item, group: label, groupName: label, label, value: percentValue }
                        })
                        .asMutable()
                        .sort((a: any, b: any) => (b.value ?? 0) - (a.value ?? 0))
                },
                extraConfigs: {
                    colors: NETPAY_COLORS_NPS,
                    decimals: 1,
                    valueSymbol: '%',
                    showValues: true,
                    wrapTexts: true,
                    rotateLabel: 90,
                    rotateXAxisLegend: true,
                    itemsWidth: 120,

                    denseBars: true,
                    showLegend: false,
                }
            }),

            // -----------------------------------------------------------
            // CSAT Y CES POR MOTIVO (B2B_ServiceLevelReason__c, id 17300)
            // -----------------------------------------------------------

            /*
            getCommonWidget({
                commonWidgets,
                name: 'barByQuestion',
                title: 'CSAT por ServiceLevelReason',
                size: [4, 5], // antes [4, 3]: más filas de alto = barras más altas verticalmente
                key: 'csat-por-motivo-b2b',
                indicator: 'np_csat_ces_satisfaccion',
                extras: {
                    formula: 'wavg-5',
                    model: 'avg',
                    groupByResponseValueId: 17300, // B2B_ServiceLevelReason__c
                    groupBy: 'responseValue',
                },
                mapper: (data: any) => {
                    return (data ?? [])
                        .map((item: any) => {
                            const rawLabel = item.group ?? item.groupName
                            const baseLabel = rawLabel ? String(rawLabel).trim() : 'No Definido'
                            // Se agrega el total de encuestas de cada motivo
                            // directo en la etiqueta (item.count), en vez de
                            // depender de showCountInLabel del widget base
                            // (esa opción no lo está calculando bien aquí).
                            const count = item.count ?? 0
                            const label = `${baseLabel} (${count})`
                            return { ...item, group: label, groupName: label, label }
                        })
                        .asMutable()
                        .sort((a: any, b: any) => (b.value ?? 0) - (a.value ?? 0))
                },
                extraConfigs: {
                    colors: NETPAY_COLORS_NPS,
                    decimals: 1,
                    showValues: true,
                    wrapTexts: true,
                    // Vertical (de arriba hacia abajo) para que se alcancen
                    // a leer los 54 nombres de motivo sin traslaparse.
                    rotateLabel: 90,
                    rotateXAxisLegend: true,
                    itemsWidth: 120,
                    denseBars: true,
                    showLegend: false,
                }
            }),
            */
            getCommonWidget({
                commonWidgets,
                name: 'barByQuestion',
                title: 'CES por ServiceLevelReason',
                size: [4, 5], // antes [4, 3]: más filas de alto = barras más altas verticalmente
                key: 'ces-por-motivo-b2b',
                indicator: 'np_csat_ces_facilidad',
                extras: {
                    formula: 'wavg-5',
                    model: 'avg',
                    groupByResponseValueId: 17300, // B2B_ServiceLevelReason__c
                    groupBy: 'responseValue',
                },
                mapper: (data: any) => {
                    return (data ?? [])
                        .map((item: any) => {
                            const rawLabel = item.group ?? item.groupName
                            const baseLabel = rawLabel ? String(rawLabel).trim() : 'No Definido'
                            const count = item.count ?? 0
                            const label = `${baseLabel} (${count})`
                            const rawValue = Number(item.value) || 0
                            const percentValue = (rawValue / 5) * 100
                            return { ...item, group: label, groupName: label, label, value: percentValue }
                        })
                        .asMutable()
                        .sort((a: any, b: any) => (b.value ?? 0) - (a.value ?? 0))
                },
                extraConfigs: {
                    colors: NETPAY_COLORS_NPS,
                    decimals: 1,
                    valueSymbol: '%', // el valor de cada barra ya viene convertido a % en el mapper
                    showValues: true,
                    wrapTexts: true,
                    rotateLabel: 90,
                    rotateXAxisLegend: true,
                    itemsWidth: 120,
                    denseBars: true,
                    showLegend: false,
                }
            }),


            // -----------------------------------------------------------
            // ETIQUETAS DE SENTIMIENTO Y COMENTARIOS
            // -----------------------------------------------------------
            { title: 'ANÁLISIS DE SENTIMIENTOS Y COMENTARIOS', type: 'label', size: [4, 1], config: { color: '#B7AADC' } },

            // --- 1. Análisis de Sentimientos Global ---
            getCommonWidget({
                commonWidgets: commonWidgets,
                name: 'textAnalysis',
                size: [4, 4], // Ajustado para evitar desbordamiento
                key: 'analisis-sentimientos-nlp-csat',
                indicator: 'sentiment',
                extraConfigs: {
                    colors: NETPAY_SCALE_3_COLORS,
                    typeColumn: 'percent',
                    widgetTitle: 'Etiquetas de Sentimientos',
                    groupKeys: ['positive', 'neutral', 'negative'],
                    groupLabels: { 'positive': 'Positivos', 'neutral': 'Neutros', 'negative': 'Negativos' },
                    defaultHeight: 600, // Aumentado para acomodar etiquetas
                },
                mapper: (data: any, extra?: any) => {
                    const actualData = Array.isArray(data) ? data[0] : data;
                    if (!actualData || !actualData.categories) return { ...actualData, categories: List([]) };

                    let categories = actualData.categories || [];
                    let sortedCategories = [...categories].sort((a: any, b: any) => b.count - a.count);

                    const splitTags = (item: any[]): any[] => {
                        const tags = [...(item || [])].sort((a: any, b: any) => b.count - a.count);
                        return tags.map((tag: any) => ({
                            ...tag,
                            tags: splitTags(tag.tags),
                        }));
                    };

                    sortedCategories = sortedCategories.map((item: any) => ({
                        ...item,
                        tags: splitTags(item.tags),
                    }));

                    return {
                        ...actualData,
                        categories: List(sortedCategories),
                    }
                },
            }),

            { title: '', type: 'label', size: [4, 1], config: { color: 'transparent' } },

            // --- Nube de Palabras ---
            { title: 'Nube de Palabras', type: 'label', size: [4, 1], config: { color: '#B7AADC' } },

            {
                title: 'Frecuencia de Palabras',
                size: [4, 2],
                config: {
                    id: 'word-count-csat-ces',
                    type: 'wordCloud',
                    mapper: (data: any, extra: any) => {
                        return data !== undefined && data.map !== undefined
                            ? data.map((item: any) => ({
                                ...item,
                                group: item.group ?? item.category
                            }))
                            : []
                    },
                    indicators: [
                        {
                            indicator: 'WORD_COUNT',
                            keyExtract: ['count', 'value', 'group', 'category'],
                            singleton: true,
                        },
                    ],
                },
            },

            // --- 2. Comentarios de Mejora ---
            {
                ...getCommonWidget({
                    commonWidgets,
                    name: 'lastComments',
                    title: 'Por favor, indícanos qué más podríamos hacer para mejorar tu experiencia con NetPay en la resolución de este ticket',
                    size: [4, 2],
                    key: 'csat-ces-comentario',
                    indicator: 'np_csat_ces_comment',
                    helpText: '<p>Muestra los comentarios más recientes del ticket.</p>',
                    extraConfigs: {
                        maxCommentSize: 200,
                        layout: 'card',
                        showScoreBadge: true,
                        scoreColors: NETPAY_SCALE_3_COLORS,
                        geolocationLabelShow: false,
                    },
                    mapper: (data: any) => {
                        // Reutilizamos el truco de CSS global para matar el scroll
                        if (typeof document !== 'undefined' && !document.getElementById('csat-comments-auto-height')) {
                            const style = document.createElement('style');
                            style.id = 'csat-comments-auto-height';
                            style.innerHTML = `
                              .csat-auto-height { height: auto !important; }
                              .csat-auto-height .epsilon-widget-card-component { height: auto !important; min-height: 100%; }
                              .csat-auto-height .comments-component--card { height: auto !important; }
                              .csat-auto-height .comments-card-list { max-height: none !important; overflow-y: visible !important; }
                          `;
                            document.head.appendChild(style);
                        }
                        return data;
                    }
                }),
                className: 'csat-auto-height'
            }
        ]
    },
} as DasboardConfig;
